import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Heart, Trash2, ShoppingCart, Package } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useWishlist } from '../../context/WishlistContext';
import { useCart } from '../../context/CartContext';
import { formatPrice } from '../../utils/currency';
import { useToast } from '../../hooks/use-toast';

const WishlistPage = () => {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const { wishlist, removeFromWishlist, isInWishlist } = useWishlist();
  const { addToCart } = useCart();
  const [loading, setLoading] = useState(false);

  const currentLang = i18n.language || 'es';

  const handleRemove = async (productId) => {
    await removeFromWishlist(productId);
  };

  const handleAddToCart = async (item) => {
    // CartContext.addToCart handles its own success/error toasts and never
    // throws. Rely on its return value so we don't display a bogus success
    // toast when the item didn't actually make it into the cart.
    return await addToCart(item.productId, 1);
  };

  const handleMoveToCart = async (item) => {
    const ok = await handleAddToCart(item);
    // Only strip it from the wishlist if it landed in the cart, otherwise
    // the user loses the item silently on any failure.
    if (ok) {
      await handleRemove(item.productId);
    }
  };

  const wishlistItems = wishlist?.items || [];

  if (loading || !wishlist) {
    return (
      <div className="bg-white rounded-lg shadow-md p-8 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#be1f1a]"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold text-[#2C2420] flex items-center gap-2">
          <Heart className="h-6 w-6 text-[#be1f1a] fill-[#be1f1a]" />
          {t('wishlist.title')}
        </h2>
        <span className="text-sm text-gray-500">
          {wishlistItems.length} {wishlistItems.length === 1 ? t('wishlist.item') : t('wishlist.items')}
        </span>
      </div>

      {wishlistItems.length === 0 ? (
        <div className="text-center py-12">
          <Heart className="h-20 w-20 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 mb-4">{t('wishlist.empty')}</p>
          <p className="text-sm text-gray-400 mb-6">
            {t('wishlist.emptyDescription')}
          </p>
          <Link to="/shop">
            <Button className="bg-[#be1f1a] hover:bg-[#a71b17]">
              {t('wishlist.browseProducts')}
            </Button>
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          {wishlistItems.map((item) => {
            const productName = item.name?.[currentLang] || item.name?.es || item.name || t('accountOrders.product');
            // Fix image URL extraction - handle both string and object formats
            let productImage = null;
            
            if (typeof item.image === 'string') {
              productImage = item.image;
            } else if (typeof item.image === 'object' && item.image) {
              productImage = item.image.thumbnail || item.image.medium || item.image.original;
            } else if (item.images?.length > 0) {
              const firstImage = item.images[0];
              if (typeof firstImage === 'string') {
                productImage = firstImage;
              } else if (firstImage) {
                productImage = firstImage.thumbnail || firstImage.medium || firstImage.original;
              }
            }

            return (
              <div
                key={item.productId}
                className="border rounded-lg p-4 hover:border-[#be1f1a] transition-colors"
              >
                <div className="flex gap-4">
                  {/* Product Image */}
                  <Link
                    to={`/product/${item.slug || item.productId}`}
                    className="flex-shrink-0"
                  >
                    <div className="w-24 h-32 bg-gray-100 rounded-lg overflow-hidden">
                      {productImage ? (
                        <img
                          src={productImage}
                          alt={productName}
                          className="w-full h-full object-cover hover:scale-105 transition-transform"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-400">
                          <Package className="h-8 w-8" />
                        </div>
                      )}
                    </div>
                  </Link>

                  {/* Product Info */}
                  <div className="flex-1">
                    <Link
                      to={`/product/${item.slug || item.productId}`}
                      className="hover:text-[#be1f1a] transition-colors"
                    >
                      <h3 className="font-semibold text-lg text-[#2C2420] mb-2">
                        {productName}
                      </h3>
                    </Link>

                    {/* Price */}
                    <div className="mb-4">
                      <p className="text-2xl font-bold text-[#be1f1a]">
                        {formatPrice(item.price)}
                      </p>
                      {item.compareAtPrice && item.compareAtPrice > item.price && (
                        <p className="text-sm text-gray-400 line-through">
                          {formatPrice(item.compareAtPrice)}
                        </p>
                      )}
                    </div>

                    {/* Stock Status */}
                    {item.inStock !== undefined && (
                      <p className={`text-sm mb-4 ${item.inStock ? 'text-green-600' : 'text-red-600'}`}>
                        {item.inStock ? `✓ ${t('wishlist.inStock')}` : `✗ ${t('wishlist.outOfStock')}`}
                      </p>
                    )}

                    {/* Actions */}
                    <div className="flex flex-wrap gap-2">
                      <Button
                        onClick={() => handleMoveToCart(item)}
                        className="bg-[#be1f1a] hover:bg-[#a71b17] text-white"
                        size="sm"
                      >
                        <ShoppingCart className="h-4 w-4 mr-2" />
                        {t('wishlist.moveToCart')}
                      </Button>
                      <Button
                        onClick={() => handleRemove(item.productId)}
                        variant="outline"
                        className="border-red-300 text-red-600 hover:bg-red-50"
                        size="sm"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        {t('wishlist.remove')}
                      </Button>
                    </div>
                  </div>

                  {/* Added Date */}
                  {item.addedAt && (
                    <div className="text-right text-xs text-gray-400">
                      {t('wishlist.added')} {new Date(item.addedAt).toLocaleDateString(currentLang.startsWith('es') ? 'es-CO' : 'en-US')}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Quick Actions */}
      {wishlistItems.length > 0 && (
        <div className="mt-6 pt-6 border-t flex justify-between items-center">
          <div className="text-sm text-gray-600">
            {wishlistItems.length} {wishlistItems.length === 1 ? t('wishlist.item') : t('wishlist.items')} {t('wishlist.inWishlist')}
          </div>
          <Link to="/shop">
            <Button variant="outline" className="text-sm">
              {t('wishlist.continueShopping')}
            </Button>
          </Link>
        </div>
      )}
    </div>
  );
};

export default WishlistPage;
